import { ALL_SPORTS, DEFAULT_RANGE, MAX_SELECTED_STATS } from '../constants'
import { getSportKey } from './sportKey'
import type {
  ProgressSport,
  StatsRole,
  StatsSelection,
  StatsSelectionAction,
} from '../types'

export const INITIAL_SELECTION: StatsSelection = {
  sportKey: null,
  role: 'skater',
  range: DEFAULT_RANGE,
  stats: [],
  colorSlots: {},
  openSeasonRowId: null,
}

/**
 * Still-offered stats stay, in selection order; with none, the defaults
 * (`pickDefaultStats`) that are offered, else the left-most chip.
 */
const resolveStats = (
  stats: string[],
  chartStats: string[],
  defaultStats: string[]
): string[] => {
  const kept = stats.filter((stat) => chartStats.includes(stat))
  if (kept.length > 0) {
    return kept
  }
  const defaults = defaultStats.filter((stat) => chartStats.includes(stat))
  if (defaults.length > 0) {
    return defaults
  }
  return chartStats.length > 0 ? [chartStats[0]] : []
}

/** No more stats can be added — one per line colour. */
export const isStatLimitReached = (stats: string[]): boolean =>
  stats.length >= MAX_SELECTED_STATS

const lowestFreeSlot = (taken: Set<number>): number => {
  let slot = 0
  while (taken.has(slot)) {
    slot += 1
  }
  return slot
}

/**
 * A colour slot for each of `stats`: a stat keeps its stored slot; one without
 * a slot (e.g. the default before any click) takes the lowest free slot, in
 * selection order. Slots of stats not in `stats` are dropped.
 */
const resolveColorSlots = (
  stats: string[],
  colorSlots: Record<string, number>
): Record<string, number> => {
  const resolved = new Map<string, number>()
  const taken = new Set<number>()
  stats.forEach((stat) => {
    const slot = colorSlots[stat]
    if (slot !== undefined && !taken.has(slot)) {
      resolved.set(stat, slot)
      taken.add(slot)
    }
  })
  stats.forEach((stat) => {
    if (!resolved.has(stat)) {
      const slot = lowestFreeSlot(taken)
      resolved.set(stat, slot)
      taken.add(slot)
    }
  })
  return Object.fromEntries(resolved)
}

/**
 * Adds an unselected stat at the end, on the lowest free colour slot — unless
 * `MAX_SELECTED_STATS` are already selected. Removes a selected one unless
 * it's the last (one stat is always charted); its slot frees up and every
 * other stat keeps its own. Toggles from the resolved stats, so the shown
 * default is kept and stats no longer offered (and their slots) are dropped.
 */
const toggleStat = (
  state: StatsSelection,
  stat: string,
  chartStats: string[],
  defaultStats: string[]
): StatsSelection => {
  const current = resolveStats(state.stats, chartStats, defaultStats)
  const currentSlots = resolveColorSlots(current, state.colorSlots)
  if (!current.includes(stat)) {
    if (isStatLimitReached(current)) {
      return state
    }
    const stats = [...current, stat]
    return {
      ...state,
      stats,
      colorSlots: resolveColorSlots(stats, currentSlots),
    }
  }
  if (current.length === 1) {
    return state
  }
  const stats = current.filter((s) => s !== stat)
  return { ...state, stats, colorSlots: resolveColorSlots(stats, currentSlots) }
}

export const statsSelectionReducer = (
  state: StatsSelection,
  action: StatsSelectionAction
): StatsSelection => {
  switch (action.type) {
    case 'setSport':
      return {
        ...state,
        sportKey: action.sportKey,
        stats: [],
        colorSlots: {},
        openSeasonRowId: null,
      }
    case 'setRole':
      return {
        ...state,
        role: action.role,
        stats: [],
        colorSlots: {},
        openSeasonRowId: null,
      }
    case 'setRange':
      return { ...state, range: action.range }
    case 'toggleStat':
      return toggleStat(
        state,
        action.stat,
        action.chartStats,
        action.defaultStats
      )
    case 'toggleSeason':
      return {
        ...state,
        openSeasonRowId:
          state.openSeasonRowId === action.rowId ? null : action.rowId,
      }
    case 'reset':
      return INITIAL_SELECTION
  }
}

interface ResolveStatsSelectionArgs {
  selection: StatsSelection
  sports: ProgressSport[]
  roles: StatsRole[]
  chartStats: string[]
  /** What to chart before the user picks; [] falls back to the first chip. */
  defaultStats?: string[]
}

const resolveSport = (
  sportKey: string | null,
  sports: ProgressSport[]
): string | null => {
  if (
    sportKey !== null &&
    (sportKey === ALL_SPORTS || sports.some((s) => getSportKey(s) === sportKey))
  ) {
    return sportKey
  }
  return sports[0] ? getSportKey(sports[0]) : null
}

export const resolveStatsSelection = ({
  selection,
  sports,
  roles,
  chartStats,
  defaultStats = [],
}: ResolveStatsSelectionArgs): StatsSelection => {
  const role = roles.includes(selection.role)
    ? selection.role
    : roles[0] ?? 'skater'
  const stats = resolveStats(selection.stats, chartStats, defaultStats)
  return {
    ...selection,
    sportKey: resolveSport(selection.sportKey, sports),
    role,
    stats,
    colorSlots: resolveColorSlots(stats, selection.colorSlots),
  }
}
